'use client';

type SignalFilter = 'all' | 'enteredAlpha' | 'exitedAlpha';

interface Props {
  signalFilter: SignalFilter;
  onSignalFilterChange: (next: SignalFilter) => void;
  enteredCount: number;
  exitedCount: number;
  /** Corner of the 3D viewport to anchor the legend. Defaults to 'top-left'
   *  (home-page convention); 'bottom-right' is used by the AlphaZoneVisualizer
   *  to clear the bottom-left alerts overlay and the top of the 3D scatter. */
  position?: 'top-left' | 'bottom-right';
}

/**
 * Legend overlay on the 3D scatter — axis hints + entered/exited
 * Alpha-Zone signal toggles (clicking a signal filters the dot view to it).
 */
export function ScatterLegend({ signalFilter, onSignalFilterChange, enteredCount, exitedCount, position = 'top-left' }: Props) {
  const signals = [
    { key: 'enteredAlpha' as const, color: '#22c55e', label: 'Entered Alpha Zone', count: enteredCount },
    { key: 'exitedAlpha' as const, color: '#dc2626', label: 'Exited Alpha Zone', count: exitedCount },
  ];

  const corner = position === 'bottom-right' ? 'bottom-3 right-3 text-right' : 'top-3 left-3';

  return (
    <div className={`absolute ${corner} z-10 font-mono text-[9px] leading-relaxed`} style={{ color: '#64748b' }}>
      <div>Origin = universe mean</div>
      <div>Shells = 1σ, 2σ, 3σ</div>
      <div style={{ color: '#60a5fa' }}>X: Quality (high = right)</div>
      <div style={{ color: '#4ade80' }}>Y: Valuation (less expensive = bottom)</div>
      <div style={{ color: '#c084fc' }}>Z: Momentum (price + revisions + surprise)</div>
      <div className="mt-2 pt-2" style={{ borderTop: '1px solid #1e293b' }}>
        {signals.map(({ key, color, label, count }) => {
          const isActive = signalFilter === key;
          return (
            <div
              key={key}
              className={`flex items-center gap-1.5 cursor-pointer ${position === 'bottom-right' ? 'justify-end' : ''}`}
              style={{ opacity: signalFilter === 'all' || isActive ? 1 : 0.3 }}
              onClick={() => onSignalFilterChange(isActive ? 'all' : key)}
            >
              {position === 'bottom-right' ? (
                <>
                  {count > 0 && <span style={{ color: '#64748b', fontSize: 8 }}>({count})</span>}
                  <span style={{ color }}>{label}</span>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', border: `2px solid ${color}`, background: isActive ? color : 'transparent' }} />
                </>
              ) : (
                <>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', border: `2px solid ${color}`, background: isActive ? color : 'transparent' }} />
                  <span style={{ color }}>{label}</span>
                  {count > 0 && <span style={{ color: '#64748b', fontSize: 9 }}>({count})</span>}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
